import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Send, MessageCircle, X, Plus, History, Eye, CheckCircle, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import ClickableMarkdown from './ClickableMarkdown';
import { salesCyclesApi } from '../services/api';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  toolCalls?: Array<{
    tool: string;
    status: 'thinking' | 'executing' | 'completed';
    result?: string;
  }>;
}

interface Chat {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  lastMessageAt: Date;
}

interface ChatWindowProps {
  isOpen: boolean;
  onToggle: () => void;
  onCollapse?: (collapsed: boolean) => void;
  currentPage?: string;
  pageContext?: string;
  isCollapsed?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ isOpen, onToggle, onCollapse, currentPage, pageContext, isCollapsed = false }) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [showCompanyAutocomplete, setShowCompanyAutocomplete] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState<{company_name: string, company_id: string}[]>([]);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [popularCompanies, setPopularCompanies] = useState<{company_name: string, company_id: string}[]>([]);
  const [selectedCompanyForProfile, setSelectedCompanyForProfile] = useState<{company_name: string, company_id: string} | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Available chat commands
  const AVAILABLE_COMMANDS = [
    {
      command: '/profile',
      description: 'Generate comprehensive customer profile',
      usage: '/profile <company name>',
      example: '/profile Microsoft'
    }
  ];

  // localStorage key for chat persistence
  const CHAT_STORAGE_KEY = 'sales-ai-chat-history';
  const CURRENT_CHAT_KEY = 'sales-ai-current-chat';

  // localStorage utility functions
  const saveChatsToStorage = (chatsToSave: Chat[]) => {
    try {
      const serializedChats = chatsToSave.map(chat => ({
        ...chat,
        createdAt: chat.createdAt.toISOString(),
        lastMessageAt: chat.lastMessageAt.toISOString(),
        messages: chat.messages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp.toISOString()
        }))
      }));
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(serializedChats));
    } catch (error) {
      console.error('Failed to save chats to localStorage:', error);
    }
  };

  const loadChatsFromStorage = (): Chat[] => {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (!stored) return [];
      
      const parsedChats = JSON.parse(stored);
      return parsedChats.map((chat: any) => ({
        ...chat,
        createdAt: new Date(chat.createdAt),
        lastMessageAt: new Date(chat.lastMessageAt),
        messages: chat.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }))
      }));
    } catch (error) {
      console.error('Failed to load chats from localStorage:', error);
      return [];
    }
  };

  const saveCurrentChatId = (chatId: string | null) => {
    try {
      if (chatId) {
        localStorage.setItem(CURRENT_CHAT_KEY, chatId);
      } else {
        localStorage.removeItem(CURRENT_CHAT_KEY);
      }
    } catch (error) {
      console.error('Failed to save current chat ID:', error);
    }
  };

  const loadCurrentChatId = (): string | null => {
    try {
      return localStorage.getItem(CURRENT_CHAT_KEY);
    } catch (error) {
      console.error('Failed to load current chat ID:', error);
      return null;
    }
  };

  const currentChat = chats.find((chat: Chat) => chat.id === currentChatId);
  const messages = useMemo(() => currentChat?.messages || [], [currentChat?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chats from localStorage on component mount
  useEffect(() => {
    const savedChats = loadChatsFromStorage();
    const savedCurrentChatId = loadCurrentChatId();

    if (savedChats.length > 0) {
      setChats(savedChats);
      // Set current chat to saved one if it exists, otherwise first chat
      const currentId = savedCurrentChatId && savedChats.find(c => c.id === savedCurrentChatId)
        ? savedCurrentChatId
        : savedChats[0].id;
      setCurrentChatId(currentId);
    } else {
      // No saved chats, create a new one
      createNewChat();
    }

    // Load popular companies for quick access
    const loadPopularCompanies = async () => {
      try {
        const companies = await salesCyclesApi.getCompanies();
        setPopularCompanies(companies.slice(0, 10)); // Top 10 companies
      } catch (error) {
        console.error('Error loading popular companies:', error);
      }
    };
    loadPopularCompanies();
  }, []); // Empty dependency array - only run on mount

  // Save chats to localStorage whenever chats change
  useEffect(() => {
    if (chats.length > 0) {
      const cleanedChats = cleanupOldChats(chats);
      if (cleanedChats.length !== chats.length) {
        // If we cleaned up chats, update the state
        setChats(cleanedChats);
      }
      saveChatsToStorage(cleanedChats);
    }
  }, [chats]);

  // Save current chat ID whenever it changes
  useEffect(() => {
    saveCurrentChatId(currentChatId);
  }, [currentChatId]);

  // Handle collapse toggle
  const handleCollapseToggle = () => {
    onCollapse?.(!isCollapsed);
  };

  const createNewChat = () => {
    const newChat: Chat = {
      id: `chat-${Date.now()}`,
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      lastMessageAt: new Date()
    };
    setChats((prev: Chat[]) => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    setShowChatHistory(false);
  };

  const selectChat = (chatId: string) => {
    setCurrentChatId(chatId);
    setShowChatHistory(false);
  };

  const clearAllChats = () => {
    if (window.confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
      setChats([]);
      setCurrentChatId(null);
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CURRENT_CHAT_KEY);
      createNewChat();
    }
  };

  const deleteChat = (chatId: string) => {
    const updatedChats = chats.filter(chat => chat.id !== chatId);
    setChats(updatedChats);
    
    // If we deleted the current chat, switch to another one or create new
    if (currentChatId === chatId) {
      if (updatedChats.length > 0) {
        setCurrentChatId(updatedChats[0].id);
      } else {
        createNewChat();
      }
    }
  };

  // Clean up old chats (keep only last 50 chats to prevent localStorage bloat)
  const cleanupOldChats = (chatsToClean: Chat[]) => {
    if (chatsToClean.length > 50) {
      const sortedChats = [...chatsToClean].sort((a, b) => 
        b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
      );
      return sortedChats.slice(0, 50);
    }
    return chatsToClean;
  };

  // Get storage usage info
  const getStorageInfo = () => {
    try {
      const used = new Blob([localStorage.getItem(CHAT_STORAGE_KEY) || '']).size;
      const total = 5 * 1024 * 1024; // 5MB typical localStorage limit
      return {
        used: Math.round(used / 1024), // KB
        total: Math.round(total / 1024), // KB
        percentage: Math.round((used / total) * 100)
      };
    } catch (error) {
      return { used: 0, total: 5120, percentage: 0 };
    }
  };

  // Export chat history in different formats
  const exportChatHistory = (format: 'text' | 'html' | 'json' | 'csv') => {
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      let content = '';
      let mimeType = '';
      let fileExtension = '';

      switch (format) {
        case 'text':
          content = generateTextExport();
          mimeType = 'text/plain';
          fileExtension = 'txt';
          break;
        case 'html':
          content = generateHTMLExport();
          mimeType = 'text/html';
          fileExtension = 'html';
          break;
        case 'csv':
          content = generateCSVExport();
          mimeType = 'text/csv';
          fileExtension = 'csv';
          break;
        case 'json':
          content = generateJSONExport();
          mimeType = 'application/json';
          fileExtension = 'json';
          break;
      }

      const dataBlob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales-ai-chat-history-${timestamp}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export chat history:', error);
      alert('Failed to export chat history. Please try again.');
    }
  };

  // Copy chat content to clipboard
  const copyToClipboard = async (format: 'text' | 'html' | 'json' | 'csv') => {
    try {
      let content = '';

      switch (format) {
        case 'text':
          content = generateTextExport();
          break;
        case 'html':
          content = generateHTMLExport();
          break;
        case 'csv':
          content = generateCSVExport();
          break;
        case 'json':
          content = generateJSONExport();
          break;
      }

      await navigator.clipboard.writeText(content);
      
      // Show success feedback
      const originalText = document.querySelector(`[data-copy-${format}]`)?.textContent;
      const button = document.querySelector(`[data-copy-${format}]`) as HTMLElement;
      if (button) {
        button.textContent = '✓ Copied!';
        button.style.color = '#10b981';
        setTimeout(() => {
          button.textContent = originalText || '';
          button.style.color = '';
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  // Copy individual message to clipboard
  const copyMessageToClipboard = async (message: Message) => {
    try {
      // Clean the message content by removing HTML tags and formatting
      const cleanContent = message.content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove markdown bold
        .replace(/\*(.*?)\*/g, '$1') // Remove markdown italic
        .replace(/`(.*?)`/g, '$1') // Remove markdown code
        .replace(/#{1,6}\s/g, '') // Remove markdown headers
        .replace(/^\s*[-*+]\s/gm, '• ') // Convert markdown lists to bullet points
        .replace(/^\s*\d+\.\s/gm, '• ') // Convert numbered lists to bullet points
        .trim();

      await navigator.clipboard.writeText(cleanContent);
      
      // Show success feedback on the specific message
      const messageElement = document.querySelector(`[data-message-id="${message.id}"]`);
      if (messageElement) {
        const copyButton = messageElement.querySelector('[data-copy-message]') as HTMLElement;
        if (copyButton) {
          const originalContent = copyButton.innerHTML;
          copyButton.innerHTML = '<CheckCircle size={12} />';
          copyButton.style.color = '#10b981';
          setTimeout(() => {
            copyButton.innerHTML = originalContent;
            copyButton.style.color = '';
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Failed to copy message to clipboard:', error);
      alert('Failed to copy message to clipboard. Please try again.');
    }
  };

  // Generate readable text export
  const generateTextExport = () => {
    let content = `SALES AI CHAT HISTORY EXPORT
Generated: ${new Date().toLocaleString()}
Total Chats: ${chats.length}

${'='.repeat(80)}

`;

    chats.forEach((chat, index) => {
      content += `CHAT ${index + 1}: ${chat.title}
Created: ${chat.createdAt.toLocaleString()}
Last Message: ${chat.lastMessageAt.toLocaleString()}
Messages: ${chat.messages.length}

${'-'.repeat(60)}

`;

      chat.messages.forEach((message, msgIndex) => {
        const sender = message.sender === 'user' ? 'SALES MANAGER' : 'AI ASSISTANT';
        const time = message.timestamp.toLocaleString();
        
        content += `[${time}] ${sender}:
${message.content}

`;

        if (message.toolCalls && message.toolCalls.length > 0) {
          content += `Tools Used: ${message.toolCalls.map(tc => tc.tool).join(', ')}

`;
        }
      });

      content += `${'='.repeat(80)}

`;
    });

    return content;
  };

  // Generate HTML export
  const generateHTMLExport = () => {
    let content = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sales AI Chat History</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@700;800&display=swap');
        
        body { 
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            line-height: 1.6; 
            margin: 0; 
            padding: 20px; 
            background: #f8fafc;
            min-height: 100vh;
        }
        
        .container { 
            max-width: 1200px; 
            margin: 0 auto; 
            display: flex;
            gap: 20px;
        }
        
        .left-panel {
            flex: 0 0 400px;
            display: flex;
            flex-direction: column;
            gap: 20px;
        }
        
        .right-panel {
            flex: 1;
            background: white;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            padding: 20px;
            min-height: 600px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .header { 
            background: white;
            color: #1e293b; 
            padding: 30px; 
            text-align: center;
            border-radius: 12px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            border: 1px solid #e2e8f0;
        }
        
        .header h1 { 
            margin: 0; 
            font-size: 24px; 
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 800;
            color: #1e293b;
        }
        
        .header p { 
            margin: 8px 0 0 0; 
            color: #64748b; 
            font-size: 16px;
        }
        
        .summary { 
            background: white;
            padding: 20px; 
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .summary h3 { 
            margin: 0 0 15px 0; 
            color: #3b82f6; 
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 18px;
        }
        
        .summary p { 
            margin: 8px 0; 
            color: #64748b;
            font-size: 14px;
        }
        
        .chat { 
            background: white;
            border-radius: 12px; 
            border: 1px solid #e2e8f0;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }
        
        .chat-header { 
            background: #f8fafc; 
            padding: 20px; 
            border-bottom: 1px solid #e2e8f0;
        }
        
        .chat-title { 
            font-size: 18px; 
            font-weight: 700; 
            color: #1e293b; 
            margin: 0 0 8px 0;
            font-family: 'Inter', sans-serif;
        }
        
        .chat-meta { 
            font-size: 14px; 
            color: #64748b;
        }
        
        .messages-container {
            padding: 20px;
            max-height: 500px;
            overflow-y: auto;
        }
        
        .message { 
            margin-bottom: 20px; 
            padding: 0;
        }
        
        .user-message { 
            display: flex;
            justify-content: flex-end;
        }
        
        .user-message .message-bubble {
            background: #3b82f6;
            color: white; 
            padding: 16px 20px;
            border-radius: 20px 20px 4px 20px;
            max-width: 70%;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .agent-message { 
            display: flex;
            justify-content: flex-start;
        }
        
        .agent-message .message-bubble {
            background: #f8fafc;
            color: #1e293b; 
            padding: 16px 20px;
            border-radius: 20px 20px 20px 4px;
            max-width: 80%;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            border: 1px solid #e2e8f0;
        }
        
        .message-header { 
            font-weight: 600; 
            margin-bottom: 8px; 
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .message-content { 
            white-space: pre-wrap; 
            font-size: 14px;
            line-height: 1.5;
        }
        
        .message-time { 
            font-size: 11px; 
            opacity: 0.7; 
            margin-top: 8px;
        }
        
        .tools-used { 
            background: #fef3c7; 
            padding: 8px 12px; 
            border-radius: 8px; 
            margin-top: 12px; 
            font-size: 12px; 
            color: #92400e;
            border: 1px solid #fde68a;
        }
        
        .tools-used::before {
            content: "🔧 ";
        }
        
        h1, h2, h3, h4, h5, h6 {
            color: #1e293b;
            font-family: 'Inter', sans-serif;
        }
        
        h1 { font-size: 24px; font-weight: 700; margin: 0 0 16px 0; }
        h2 { font-size: 20px; font-weight: 600; margin: 0 0 12px 0; color: #3b82f6; }
        h3 { font-size: 18px; font-weight: 600; margin: 0 0 10px 0; }
        
        ul, ol {
            margin: 8px 0;
            padding-left: 20px;
        }
        
        li {
            margin: 4px 0;
        }
        
        strong {
            font-weight: 600;
        }
        
        code {
            background: rgba(0, 0, 0, 0.1);
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 13px;
        }
        
        blockquote {
            border-left: 4px solid #3b82f6;
            margin: 12px 0;
            padding-left: 16px;
            color: #64748b;
            font-style: italic;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="left-panel">
            <div class="header">
                <h1>Sales AI Chat History</h1>
                <p>Generated: ${new Date().toLocaleString()}</p>
                <p>Total Chats: ${chats.length}</p>
            </div>

            <div class="summary">
                <h3>📊 Export Summary</h3>
                <p><strong>Total Conversations:</strong> ${chats.length}</p>
                <p><strong>Total Messages:</strong> ${chats.reduce((sum, chat) => sum + chat.messages.length, 0)}</p>
                <p><strong>Date Range:</strong> ${new Date(Math.min(...chats.map(c => c.createdAt.getTime()))).toLocaleDateString()} - ${new Date(Math.max(...chats.map(c => c.lastMessageAt.getTime()))).toLocaleDateString()}</p>
            </div>
`;

    // Add chat list to left panel
    chats.forEach((chat, index) => {
      content += `
            <div class="chat">
                <div class="chat-header">
                    <div class="chat-title">${chat.title}</div>
                    <div class="chat-meta">
                        Created: ${chat.createdAt.toLocaleString()} | 
                        Last Message: ${chat.lastMessageAt.toLocaleString()} | 
                        Messages: ${chat.messages.length}
                    </div>
                </div>
            </div>
`;
    });

    content += `        </div>

        <div class="right-panel">
`;

    // Show first chat's messages in right panel
    if (chats.length > 0) {
      const firstChat = chats[0];
      content += `
            <div class="messages-container">
`;

      firstChat.messages.forEach((message) => {
        const sender = message.sender === 'user' ? 'SALES MANAGER' : 'AI ASSISTANT';
        const messageClass = message.sender === 'user' ? 'user-message' : 'agent-message';
        
        content += `
                <div class="message ${messageClass}">
                    <div class="message-bubble">
                        <div class="message-header">${sender}</div>
                        <div class="message-content">${message.content}</div>
                        <div class="message-time">${message.timestamp.toLocaleString()}</div>
`;

        if (message.toolCalls && message.toolCalls.length > 0) {
          content += `
                        <div class="tools-used">
                            Tools Used: ${message.toolCalls.map(tc => tc.tool).join(', ')}
                        </div>
`;
        }

        content += `                    </div>
                </div>
`;
      });

      content += `            </div>
`;
    }

    content += `
    </div>
</body>
</html>`;

    return content;
  };

  // Generate CSV export
  const generateCSVExport = () => {
    let content = 'Chat ID,Chat Title,Created Date,Last Message Date,Message Count,Message Sender,Message Content,Message Time,Tools Used\n';
    
    chats.forEach(chat => {
      chat.messages.forEach(message => {
        const toolsUsed = message.toolCalls ? message.toolCalls.map(tc => tc.tool).join('; ') : '';
        const contentEscaped = `"${message.content.replace(/"/g, '""')}"`;
        
        content += `${chat.id},"${chat.title}",${chat.createdAt.toISOString()},${chat.lastMessageAt.toISOString()},${chat.messages.length},${message.sender},${contentEscaped},${message.timestamp.toISOString()},"${toolsUsed}"\n`;
      });
    });
    
    return content;
  };

  // Generate user-friendly JSON export
  const generateJSONExport = () => {
    const exportData = {
      export_info: {
        generated_at: new Date().toISOString(),
        total_chats: chats.length,
        total_messages: chats.reduce((sum, chat) => sum + chat.messages.length, 0),
        export_format: "Sales AI Chat History - User Friendly JSON",
        description: "Complete chat history with readable timestamps and tool usage information"
      },
      conversations: chats.map(chat => ({
        chat_id: chat.id,
        title: chat.title,
        created_at: chat.createdAt.toLocaleString(),
        last_message_at: chat.lastMessageAt.toLocaleString(),
        message_count: chat.messages.length,
        messages: chat.messages.map(message => ({
          id: message.id,
          sender: message.sender === 'user' ? 'Sales Manager' : 'AI Assistant',
          content: message.content,
          timestamp: message.timestamp.toLocaleString(),
          tools_used: message.toolCalls ? message.toolCalls.map(tc => ({
            tool_name: tc.tool,
            status: tc.status,
            description: getToolDescription(tc.tool)
          })) : []
        }))
      }))
    };
    
    return JSON.stringify(exportData, null, 2);
  };

  // Get human-readable tool descriptions
  const getToolDescription = (toolName: string): string => {
    const toolDescriptions: { [key: string]: string } = {
      'get_company_data': 'Retrieved company information and deal history',
      'weaviate_search_all_collections': 'Searched AI knowledge base for insights',
      'get_sales_overview': 'Analyzed sales performance metrics',
      'analyze_trends': 'Examined sales trends and patterns',
      'get_at_risk_deals': 'Identified at-risk deals',
      'get_deals_by_stage': 'Filtered deals by sales stage',
      'get_team_performance': 'Analyzed team performance data',
      'get_rep_performance': 'Examined individual rep performance',
      'get_closed_won_insights': 'Analyzed successful deal patterns',
      'get_closed_lost_insights': 'Examined lost deal patterns'
    };
    
    return toolDescriptions[toolName] || `Used ${toolName} tool`;
  };


  const updateChatTitle = (chatId: string, firstMessage: string) => {
    const title = firstMessage.length > 30 ? firstMessage.substring(0, 30) + '...' : firstMessage;
    setChats((prev: Chat[]) => prev.map((chat: Chat) => 
      chat.id === chatId ? { ...chat, title } : chat
    ));
  };

  const updateChatMessages = (chatId: string, newMessages: Message[]) => {
    setChats((prev: Chat[]) => prev.map((chat: Chat) => 
      chat.id === chatId 
        ? { ...chat, messages: newMessages, lastMessageAt: new Date() }
        : chat
    ));
  };


  // Vector database is fixed to MCP - no switching needed



  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // If this is a /profile command with selected company, replace name with ID for backend
    let messageToSend = inputMessage;
    if (selectedCompanyForProfile && inputMessage.startsWith('/profile ')) {
      messageToSend = `/profile ${selectedCompanyForProfile.company_id}`;
      // Clear the stored company after using it
      setSelectedCompanyForProfile(null);
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content: inputMessage, // Show company name in UI
      sender: 'user',
      timestamp: new Date()
    };

    const newMessages = [...messages, userMessage];
    if (currentChatId) {
      updateChatMessages(currentChatId, newMessages);
      if (messages.length === 0) {
        updateChatTitle(currentChatId, inputMessage);
      }
    }

    const messageText = messageToSend; // Send company_id to backend
    setInputMessage('');
    setIsLoading(true);

    // Create a streaming agent message
    const streamingAgentMessage: Message = {
      id: `agent-${Date.now()}`,
      content: '',
      sender: 'agent',
      timestamp: new Date(),
      toolCalls: []
    };

    const messagesWithStreaming = [...newMessages, streamingAgentMessage];
    if (currentChatId) {
      updateChatMessages(currentChatId, messagesWithStreaming);
    }

    // Track whether we've received any response data
    let timeoutId: NodeJS.Timeout | undefined;
    let hasReceivedContent = false;

    // Create AbortController for request timeout
    const STREAMING_TIMEOUT_MS = 600000; // 10 minutes timeout for complex multi-tool queries
    const abortController = new AbortController();
    const abortTimeoutId = setTimeout(() => {
      console.log('⏰ Request timeout - aborting after', STREAMING_TIMEOUT_MS / 1000, 'seconds');
      abortController.abort();
    }, STREAMING_TIMEOUT_MS);

    try {
      // Use the same API URL logic as the main API service
      const getApiBaseUrl = () => {
        if (process.env.REACT_APP_API_URL) {
          return process.env.REACT_APP_API_URL;
        }
        return 'http://localhost:8001';
      };

      const API_BASE_URL = getApiBaseUrl();

      console.log('🚀 Starting streaming request to:', `${API_BASE_URL}/api/v1/chat/message/stream`);

      const response = await fetch(`${API_BASE_URL}/api/v1/chat/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('salesai-api-key')}`,
        },
        body: JSON.stringify({
          message: messageText,
          conversation_history: newMessages.map(m => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.content
          })),
          current_page: currentPage,
          page_context: pageContext,
          user_session_id: currentChatId
        }),
        signal: abortController.signal,
      });

      console.log('📡 Response received:', response.status, response.headers.get('content-type'));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not OK:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        console.error('❌ No response body for streaming');
        throw new Error('No response body for streaming');
      }

      console.log('✅ Starting to read streaming response...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamingContent = '';
      const toolCallsMap = new Map<string, any>();

      while (true) {
        const { done, value } = await reader.read();
        console.log('📝 Read chunk:', done ? 'DONE' : `${value.length} bytes`);
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        console.log('📄 Processing', lines.length, 'lines');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            console.log('📦 Raw data:', data);
            
            if (data === '[DONE]') {
              console.log('🏁 Stream completed');
              break;
            }

            try {
              const chunk = JSON.parse(data);
              console.log('✨ Parsed chunk:', chunk);
              
              if (chunk.type === 'tool_call') {
                console.log('🔧 Tool call:', chunk.tool, chunk.status);
                toolCallsMap.set(chunk.tool, {
                  tool: chunk.tool,
                  status: chunk.status,
                  result: null
                });
              } else if (chunk.type === 'content') {
                console.log('📝 Content received:', chunk.content);
                // Replace streaming content with the latest chunk (since backend sends complete content)
                if (chunk.content && chunk.content.trim()) {
                  streamingContent = chunk.content;
                  hasReceivedContent = true;
                  if (timeoutId) clearTimeout(timeoutId);
                }
              } else if (chunk.type === 'info') {
                console.log('ℹ️ Info message:', chunk.message);
                // Show info messages as temporary content
                if (chunk.message && chunk.message.trim()) {
                  streamingContent = chunk.message;
                  hasReceivedContent = true;
                  if (timeoutId) clearTimeout(timeoutId);
                }
              } else if (chunk.type === 'final_response') {
                console.log('🎯 Final response received');
                streamingContent = chunk.response;
                hasReceivedContent = true;
                if (timeoutId) clearTimeout(timeoutId);
                // Final tool calls update
                if (chunk.tool_calls) {
                  chunk.tool_calls.forEach((tc: any) => {
                    toolCallsMap.set(tc.tool, tc);
                  });
                }
              } else if (chunk.type === 'error') {
                console.error('❌ Error chunk:', chunk.message);
                if (timeoutId) clearTimeout(timeoutId);
                throw new Error(chunk.message || 'Streaming error occurred');
              } else if (chunk.type === 'status') {
                console.log('ℹ️ Status:', chunk.message);
              }

              // Always update the message, even with empty content to show tool calls
              const updatedStreamingMessage = {
                ...streamingAgentMessage,
                content: streamingContent || '',
                toolCalls: Array.from(toolCallsMap.values())
              };

              console.log('🔄 Updating message with content:', updatedStreamingMessage.content.substring(0, 100));

              const updatedMessagesWithStream = [...newMessages, updatedStreamingMessage];
              if (currentChatId) {
                updateChatMessages(currentChatId, updatedMessagesWithStream);
              }

            } catch (e) {
              console.warn('⚠️ Failed to parse streaming chunk:', data, e);
            }
          }
        }
      }

    } catch (error) {
      console.error('Chat streaming error:', error);
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(abortTimeoutId); // Clear abort timeout on error

      // Handle abort errors specially (timeout)
      let errorContent = '';
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorContent = 'Request timed out after 10 minutes. The query took too long to process. Please try a simpler query or try again.';
        } else {
          errorContent = `Error: ${error.message}`;
        }
      } else {
        errorContent = 'Sorry, I encountered an error. Please try again.';
      }

      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        content: errorContent,
        sender: 'agent',
        timestamp: new Date()
      };
      const updatedMessages = [...newMessages, errorMessage];
      if (currentChatId) {
        updateChatMessages(currentChatId, updatedMessages);
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(abortTimeoutId); // Ensure abort timeout is cleared
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle company autocomplete navigation
    const displayedCompanies = companySuggestions.length > 0 ? companySuggestions : popularCompanies;
    if (showCompanyAutocomplete && displayedCompanies.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCompanyIndex((prev) =>
          prev < displayedCompanies.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCompanyIndex((prev) => (prev > 0 ? prev - 1 : 0));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        handleCompanySelect(displayedCompanies[selectedCompanyIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCompanyAutocomplete(false);
        setCompanySuggestions([]);
        return;
      }
    }

    // Handle command menu navigation
    if (showCommandMenu) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex((prev) => (prev > 0 ? prev - 1 : 0));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && filteredCommands.length > 0)) {
        e.preventDefault();
        handleCommandSelect(filteredCommands[selectedCommandIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCommandMenu(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Filter commands based on input
  const filteredCommands = AVAILABLE_COMMANDS.filter(cmd => {
    if (!showCommandMenu) return false;
    const input = inputMessage.toLowerCase();
    return cmd.command.toLowerCase().startsWith(input);
  });

  // Handle command selection from menu
  const handleCommandSelect = (command: typeof AVAILABLE_COMMANDS[0]) => {
    setInputMessage(command.command + ' ');
    setShowCommandMenu(false);
    setSelectedCommandIndex(0);

    // Refocus the input and move cursor to end
    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        // Move cursor to end
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }, 0);
  };

  // Fetch company suggestions based on search query
  const fetchCompanySuggestions = async (searchQuery: string) => {
    // If query is too short, clear suggestions but don't hide the menu
    if (!searchQuery || searchQuery.trim().length < 2) {
      setCompanySuggestions([]);
      setIsLoadingCompanies(false);
      return;
    }

    setIsLoadingCompanies(true);
    try {
      const companies = await salesCyclesApi.getCompanies(searchQuery.trim());
      setCompanySuggestions(companies);
      setSelectedCompanyIndex(0);
    } catch (error) {
      console.error('Error fetching company suggestions:', error);
      setCompanySuggestions([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // Handle company selection from autocomplete
  const handleCompanySelect = (company: {company_name: string, company_id: string}) => {
    // Display company name in input (user-friendly), but store company for backend
    setInputMessage(`/profile ${company.company_name}`);
    setSelectedCompanyForProfile(company);
    setShowCompanyAutocomplete(false);
    setCompanySuggestions([]);
    setSelectedCompanyIndex(0);

    // Refocus the input and move cursor to end
    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        // Move cursor to end
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }, 0);
  };

  // Handle input change and detect command/company trigger
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputMessage(value);

    // Clear stored company if user modifies the /profile message
    if (selectedCompanyForProfile && !value.startsWith('/profile ')) {
      setSelectedCompanyForProfile(null);
    }

    // Show command menu when user types '/' at the start
    if (value === '/' || (value.startsWith('/') && !value.includes(' '))) {
      setShowCommandMenu(true);
      setSelectedCommandIndex(0);
      setShowCompanyAutocomplete(false);
    } else {
      setShowCommandMenu(false);

      // Show company autocomplete when user types after /profile command
      if (value.startsWith('/profile ')) {
        const companyQuery = value.substring('/profile '.length);
        setShowCompanyAutocomplete(true);
        fetchCompanySuggestions(companyQuery);
      } else {
        setShowCompanyAutocomplete(false);
        setCompanySuggestions([]);
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-6 right-6 bg-accent-blue hover:bg-accent-blue/80 text-white rounded-full p-4 shadow-soft transition-all duration-200 z-50"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className={`fixed top-0 right-0 h-screen bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ease-in-out z-30 ${
      isCollapsed ? 'w-12' : 'w-[480px]'
    }`}>
      {/* Collapse/Expand Button */}
              <button
          onClick={handleCollapseToggle}
          className="absolute -left-3 top-6 bg-accent-blue hover:bg-accent-blue/80 text-white rounded-full p-2 shadow-soft transition-all duration-200 z-50"
          title={isCollapsed ? "Expand chat" : "Collapse chat"}
        >
        {isCollapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
      </button>

      {isCollapsed ? (
        // Collapsed state - minimal interface
        <div className="flex flex-col items-center justify-center h-full space-y-4">
          <div className="text-center">
            <MessageCircle size={24} className="text-brand-600 mx-auto mb-2" />
            <p className="text-xs text-brand-600 font-medium">Chat</p>
          </div>
          <div className="text-center">
            <button
              onClick={createNewChat}
              className="bg-accent-blue hover:bg-accent-blue/80 text-white rounded-full p-2 shadow-soft transition-all duration-200"
              title="New Chat"
            >
              <Plus size={16} />
            </button>
            <p className="text-xs text-brand-600 mt-1">New</p>
          </div>
          {chats.length > 1 && (
            <div className="text-center">
              <button
                onClick={() => setShowChatHistory(!showChatHistory)}
                className="bg-brand-100 hover:bg-brand-200 text-brand-600 rounded-full p-2 transition-all duration-200"
                title="Chat History"
              >
                <History size={16} />
              </button>
              <p className="text-xs text-brand-600 mt-1">{chats.length}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800">
                                      <div className="flex items-center space-x-2 flex-1">
              <MessageCircle size={20} className="text-brand-600 ml-2" />
              <div className="flex-1">
                  <h3 className="font-semibold text-sm text-brand-900">{currentChat?.title || 'Sales Data Assistant (HOT RELOAD TEST)'}</h3>
                {currentChat && chats.length > 1 && (
                  <p className="text-xs text-brand-600">{chats.length} chats</p>
                )}
              </div>
            </div>
                         <div className="flex items-center space-x-1">
               <button
                 onClick={createNewChat}
                 className="hover:bg-brand-100 p-1 rounded transition-colors text-brand-600 hover:text-brand-900"
                 title="New Chat"
               >
                 <Plus size={16} />
               </button>
               <div className="relative">
                 <button
                   onClick={() => setShowChatHistory(!showChatHistory)}
                   onMouseEnter={() => setShowChatHistory(true)}
                   className="hover:bg-brand-100 p-1 rounded transition-colors text-brand-600 hover:text-brand-900"
                   title="Chat History"
                 >
                   <History size={16} />
                 </button>
                 {showChatHistory && (
                   <div 
                     className="absolute top-8 right-0 w-64 bg-white dark:bg-gray-800 text-brand-900 border border-card-border rounded-lg shadow-soft z-[10000] max-h-60 overflow-y-auto"
                     onMouseEnter={() => setShowChatHistory(true)}
                     onMouseLeave={() => setShowChatHistory(false)}
                   >
                     <div className="p-2 border-b border-card-border">
                       <div className="flex items-center justify-between mb-2">
                         <p className="text-xs font-semibold text-brand-600">CHAT HISTORY</p>
                         {chats.length > 0 && (
                           <button
                             onClick={clearAllChats}
                             className="text-xs text-red-500 hover:text-red-700 font-medium"
                             title="Clear all chats"
                           >
                             Clear All
                           </button>
                         )}
                       </div>
                       {chats.length > 0 && (
                         <div className="flex space-x-2">
                           <div
                             className="relative group"
                             onMouseEnter={() => setShowExportMenu(true)}
                             onMouseLeave={() => setShowExportMenu(false)}
                           >
                             <button
                               className="text-xs text-accent-blue hover:text-accent-blue/80 font-medium px-2 py-1 rounded bg-accent-blue/10 hover:bg-accent-blue/20 transition-colors flex items-center space-x-1"
                               title="Export chat history"
                             >
                               <span>Export</span>
                               <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                               </svg>
                             </button>
                             {showExportMenu && (
                               <div
                                 className="absolute top-full left-0 bg-white dark:bg-gray-800 border border-card-border rounded-lg shadow-soft z-[10001] min-w-40"
                               >
                               <div className="px-3 py-2 text-xs font-semibold text-brand-700 border-b border-card-border bg-brand-50">
                                 📥 Download
                               </div>
                               <button
                                 onClick={() => exportChatHistory('text')}
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 📄 Text (.txt)
                               </button>
                               <button
                                 onClick={() => exportChatHistory('html')}
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 🌐 HTML (.html)
                               </button>
                               <button
                                 onClick={() => exportChatHistory('csv')}
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 📊 CSV (.csv)
                               </button>
                               <button
                                 onClick={() => exportChatHistory('json')}
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 ⚙️ JSON (.json)
                               </button>
                               <div className="px-3 py-2 text-xs font-semibold text-brand-700 border-b border-card-border bg-brand-50">
                                 📋 Copy to Clipboard
                               </div>
                               <button
                                 onClick={() => copyToClipboard('text')}
                                 data-copy-text="📄 Copy Text"
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 📄 Copy Text
                               </button>
                               <button
                                 onClick={() => copyToClipboard('html')}
                                 data-copy-html="🌐 Copy HTML"
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 🌐 Copy HTML
                               </button>
                               <button
                                 onClick={() => copyToClipboard('csv')}
                                 data-copy-csv="📊 Copy CSV"
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 border-b border-card-border transition-colors"
                               >
                                 📊 Copy CSV
                               </button>
                               <button
                                 onClick={() => copyToClipboard('json')}
                                 data-copy-json="⚙️ Copy JSON"
                                 className="block w-full text-left text-xs text-brand-900 hover:bg-brand-50 px-3 py-2 transition-colors"
                               >
                                 ⚙️ Copy JSON
                               </button>
                             </div>
                             )}
                           </div>
                         </div>
                       )}
                     </div>
                     {chats.map((chat) => (
                       <div
                         key={chat.id}
                         className={`flex items-center group hover:bg-brand-50 border-b border-card-border last:border-b-0 transition-colors ${
                           chat.id === currentChatId ? 'bg-accent-blue/10 border-l-2 border-l-accent-blue' : ''
                         }`}
                       >
                         <button
                           onClick={() => selectChat(chat.id)}
                           className="flex-1 text-left p-3 min-w-0"
                         >
                           <p className="text-sm font-medium text-brand-900 truncate">{chat.title}</p>
                           <p className="text-xs text-brand-600 mt-1">
                             {chat.messages.length} messages • {chat.lastMessageAt.toLocaleDateString()}
                           </p>
                         </button>
                         {chats.length > 1 && (
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               deleteChat(chat.id);
                             }}
                             className="opacity-0 group-hover:opacity-100 p-2 text-red-500 hover:text-red-700 transition-opacity"
                             title="Delete chat"
                           >
                             <X size={12} />
                           </button>
                         )}
                       </div>
                     ))}
                     {chats.length > 0 && (
                       <div className="p-2 border-t border-card-border bg-brand-50">
                         <div className="text-xs text-brand-600">
                           <div className="flex justify-between items-center mb-1">
                             <span>Storage Used:</span>
                             <span>{getStorageInfo().used}KB / {getStorageInfo().total}KB</span>
                           </div>
                           <div className="w-full bg-brand-200 rounded-full h-1.5">
                             <div 
                               className="bg-accent-blue h-1.5 rounded-full transition-all duration-300"
                               style={{ width: `${getStorageInfo().percentage}%` }}
                             ></div>
                           </div>
                           <p className="text-xs text-brand-500 mt-1">
                             Auto-cleanup keeps last 50 chats • Data persists until browser cache is cleared
                           </p>
                         </div>
                       </div>
                     )}
                   </div>
                 )}
               </div>
               <button
                 onClick={onToggle}
                 className="hover:bg-brand-100 p-1 rounded transition-colors text-brand-600 hover:text-brand-900"
               >
                 <X size={16} />
               </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white dark:bg-gray-800">
            {messages.length === 0 && (
              <div className="text-center text-brand-600 mt-8">
                <MessageCircle size={48} className="mx-auto mb-4 text-brand-600" />
                <p className="text-sm font-medium">Ask me anything about your sales data!</p>
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-brand-600 font-medium">Try asking:</p>
                  <div className="space-y-1">
                    {[
                      "Show me deals with scores below 2.5 out of 5.0",
                      "Which cycles are at risk of hitting the 150-day rule?", 
                      "Find deals missing Economic Buyer identification",
                      "Show me recent meetings with negative sentiment",
                      "Which deals have AI red flags?",
                      "Analyze sales history for Johnson & Johnson"
                    ].map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="block w-full text-left text-xs text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10 p-2 rounded transition-colors font-medium"
                      >
                        "{suggestion}"
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                data-message-id={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 relative group ${
                    message.sender === 'user'
                      ? 'bg-accent-blue text-white shadow-soft'
                      : 'bg-white dark:bg-gray-800 text-brand-900 border border-card-border shadow-soft'
                  }`}
                >
                  {/* Copy button for individual messages */}
                  <button
                    onClick={() => copyMessageToClipboard(message)}
                    data-copy-message
                    className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded ${
                      message.sender === 'user'
                        ? 'text-white/70 hover:text-white hover:bg-white/20'
                        : 'text-brand-500 hover:text-brand-700 hover:bg-brand-100'
                    }`}
                    title="Copy message to clipboard"
                  >
                    <Copy size={12} />
                  </button>
                  {message.sender === 'agent' ? (
                    <div className="text-sm">
                      {/* Tool Execution Display */}
                      {message.toolCalls && message.toolCalls.length > 0 && (
                        <div className="mb-3 p-2 bg-brand-100 rounded border border-brand-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Eye size={14} className="text-brand-500" />
                            <span className="text-xs font-medium text-brand-600">Tool Execution</span>
                          </div>
                          {message.toolCalls.map((toolCall, index) => (
                            <div key={index} className="flex items-center gap-2 text-xs text-brand-600 mb-1">
                              {toolCall.status === 'thinking' && (
                                <>
                                  <div className="w-2 h-2 bg-warning-400 rounded-full animate-pulse"></div>
                                  <span>Thinking about using {toolCall.tool}</span>
                                </>
                              )}
                              {toolCall.status === 'executing' && (
                                <>
                                  <div className="w-2 h-2 bg-accent-blue rounded-full animate-spin"></div>
                                  <span>Executing {toolCall.tool}</span>
                                </>
                              )}
                              {toolCall.status === 'completed' && (
                                <>
                                  <CheckCircle size={12} className="text-success-600" />
                                  <span>Used {toolCall.tool}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Message Content with Clickable IDs */}
                      <ClickableMarkdown 
                        content={message.content}
                        className="markdown-content"
                      />
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap font-medium">{message.content}</p>
                  )}
                  <p className={`text-xs mt-1 ${
                    message.sender === 'user' ? 'text-white/80' : 'text-brand-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-card-border shadow-soft">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="bg-white dark:bg-gray-800 px-3 pb-3 flex-shrink-0">
            <div className="relative">
              {/* Company Autocomplete Menu */}
              {showCompanyAutocomplete && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-card-border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                  <div className="p-2 border-b border-card-border bg-brand-50">
                    <p className="text-xs font-semibold text-brand-600">
                      {companySuggestions.length > 0 ? 'SEARCH RESULTS' : 'POPULAR COMPANIES'}
                    </p>
                  </div>
                  {isLoadingCompanies ? (
                    <div className="p-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-accent-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <p className="text-xs text-brand-500 mt-2">Searching companies...</p>
                    </div>
                  ) : (companySuggestions.length > 0 || popularCompanies.length > 0) ? (
                    <>
                      {(companySuggestions.length > 0 ? companySuggestions : popularCompanies).map((company, index) => (
                        <button
                          key={company.company_id}
                          onClick={() => handleCompanySelect(company)}
                          className={`w-full text-left p-3 border-b border-card-border last:border-b-0 transition-colors ${
                            index === selectedCompanyIndex
                              ? 'bg-accent-blue/10 border-l-2 border-l-accent-blue'
                              : 'hover:bg-brand-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-brand-900">{company.company_name}</span>
                            {index === selectedCompanyIndex && (
                              <span className="text-xs text-brand-500">↵ Enter</span>
                            )}
                          </div>
                        </button>
                      ))}
                      <div className="p-2 bg-brand-50 border-t border-card-border">
                        <p className="text-xs text-brand-500">
                          {companySuggestions.length === 0
                            ? 'Type to search or select from popular companies • ↑↓ to navigate'
                            : 'Use ↑↓ to navigate • Tab or Enter to select • Esc to close'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-xs text-brand-500">Loading companies...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Command Menu Popup */}
              {showCommandMenu && filteredCommands.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-card-border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                  <div className="p-2 border-b border-card-border bg-brand-50">
                    <p className="text-xs font-semibold text-brand-600">AVAILABLE COMMANDS</p>
                  </div>
                  {filteredCommands.map((cmd, index) => (
                    <button
                      key={cmd.command}
                      onClick={() => handleCommandSelect(cmd)}
                      className={`w-full text-left p-3 border-b border-card-border last:border-b-0 transition-colors ${
                        index === selectedCommandIndex
                          ? 'bg-accent-blue/10 border-l-2 border-l-accent-blue'
                          : 'hover:bg-brand-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-brand-900">{cmd.command}</span>
                        <span className="text-xs text-brand-500">
                          {index === selectedCommandIndex ? '↵ Enter' : ''}
                        </span>
                      </div>
                      <p className="text-xs text-brand-600 mb-1">{cmd.description}</p>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-brand-500">Usage:</span>
                        <code className="text-xs bg-brand-100 px-2 py-0.5 rounded text-brand-700">
                          {cmd.usage}
                        </code>
                      </div>
                      <p className="text-xs text-brand-500 mt-1">
                        Example: <span className="text-accent-blue font-medium">{cmd.example}</span>
                      </p>
                    </button>
                  ))}
                  <div className="p-2 bg-brand-50 border-t border-card-border">
                    <p className="text-xs text-brand-500">
                      Use ↑↓ to navigate • Tab or Enter to select • Esc to close
                    </p>
                  </div>
                </div>
              )}

              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your sales data... (type / for commands)"
                className="w-full bg-brand-50 border border-card-border rounded-lg px-3 py-2 pr-12 text-sm text-brand-900 placeholder-brand-500 focus:outline-none focus:ring-1 focus:ring-accent-blue focus:border-transparent font-medium resize-none"
                disabled={isLoading}
                rows={2}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="absolute right-2 bottom-2 bg-accent-blue hover:bg-accent-blue/80 disabled:bg-brand-400 text-white rounded-full p-2 transition-all duration-200 flex items-center justify-center"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;